// src/services/crcNames.js
//
// "CRC names" (UI revamp, 2026-11-02): links the full names written on studies
// (Lead CRC, e.g. "JALAK UPADHYAY") to coordinators on Manage Coordinators
// (short names, e.g. "Jalak"). The coordinator list is NOT touched (its names
// are the key for boxes, weekly reports and leaderboards), and NO study is
// changed: studies keep their full names; this list tells the program who they
// are. New studies pick a full name from this list (LeadCrcInput).
//
// A link: { id, fullName, coordinator, sameAsCoordinator }. Pure: no Firebase.

import { compareTokens } from "./patientDuplicateMatch";

/** Capitals and extra spaces never matter: "JALAK  UPADHYAY " = "jalak upadhyay". */
export const normName = (s) => String(s || "").trim().replace(/\s+/g, " ").toLowerCase();

/** One link per full name: the document id is the normalized full name. */
export const crcNameId = (fullName) => encodeURIComponent(normName(fullName));

const coordNames = (c) =>
  [c && c.name, ...((c && Array.isArray(c.nameHistory) && c.nameHistory) || [])].map(normName).filter(Boolean);

/**
 * The coordinator (their name on Manage Coordinators) a study's Lead CRC means,
 * or "" when it isn't linked: a full name on the CRC names list, or exactly a
 * coordinator's name (or earlier name). Exact only; nothing is guessed.
 */
export const coordinatorForLead = (lead, links = [], roster = []) => {
  const want = normName(lead);
  if (!want) return "";
  const link = (links || []).find((l) => l && normName(l.fullName) === want);
  if (link) return link.coordinator || "";
  const c = (roster || []).find((r) => r && r.name && coordNames(r).includes(want));
  return c ? c.name : "";
};

const isActive = (c) => c && c.name && c.active !== false;

/**
 * A suggested coordinator for a full name on studies: the coordinator whose
 * name is its FIRST word (exactly, or the same name as a nickname: "Chris" for
 * "CHRISTOPHER BOPP"), when exactly ONE person on the whole list fits
 * (people who left included) and that one is active. Otherwise null. Only a
 * suggestion: an admin confirms every link.
 */
export const suggestCoordinator = (fullName, roster = []) => {
  const first = normName(fullName).replace(/,/g, " ").split(" ")[0] || "";
  if (!first) return null;
  const fits = (roster || []).filter((c) => {
    if (!c || !c.name) return false;
    return coordNames(c).some((n) => {
      const word = n.split(" ")[0];
      const rel = compareTokens(word, first);
      return rel === "exact" || rel === "nickname";
    });
  });
  return fits.length === 1 && isActive(fits[0]) ? fits[0].name : null;
};

/**
 * Full names on studies that aren't linked yet (and aren't a coordinator's own
 * name), with how many studies use each and a suggestion. Most-used first.
 * The name is kept as first seen on a study. -> [{ fullName, studies, suggestion }]
 */
export const unlinkedStudyNames = (studies = [], links = [], roster = []) => {
  const byKey = new Map();
  (studies || []).forEach((s) => {
    const raw = String((s && s.LEAD_CRC) || "").trim();
    if (!raw || coordinatorForLead(raw, links, roster)) return;
    const key = normName(raw);
    if (!byKey.has(key)) byKey.set(key, { fullName: raw, studies: 0 });
    byKey.get(key).studies += 1;
  });
  return [...byKey.values()]
    .map((x) => ({ ...x, suggestion: suggestCoordinator(x.fullName, roster) }))
    .sort((a, b) => b.studies - a.studies || a.fullName.localeCompare(b.fullName));
};

/** The Lead CRC dropdown: full names linked to an ACTIVE coordinator, as written on the list. */
export const dropdownNames = (links = [], roster = []) => {
  const active = new Set((roster || []).filter(isActive).map((c) => c.name));
  return [...new Set((links || []).filter((l) => l && active.has(l.coordinator)).map((l) => l.fullName))].sort((a, b) =>
    a.localeCompare(b)
  );
};

/**
 * Check a link before saving -> { ok, link } or { ok: false, error }.
 * With `sameAsCoordinator`, the full name IS the coordinator's name.
 */
export const validateLink = ({ fullName, coordinator, sameAsCoordinator }, roster = [], links = [], { editingId } = {}) => {
  const coord = (roster || []).find((c) => c && c.name === coordinator);
  if (!coord) return { ok: false, error: "Pick a coordinator from the list." };
  const name = sameAsCoordinator ? coord.name : String(fullName || "").trim().replace(/\s+/g, " ");
  if (!name) return { ok: false, error: "Type the full name as it should show on studies." };
  if (name.length > 80) return { ok: false, error: "Names can be at most 80 characters." };
  const id = crcNameId(name);
  const taken = (links || []).find((l) => l && l.id === id && l.id !== editingId);
  if (taken) return { ok: false, error: `"${taken.fullName}" is already linked to ${taken.coordinator}.` };
  return { ok: true, link: { id, fullName: name, coordinator: coord.name, sameAsCoordinator: !!sameAsCoordinator } };
};
